import { Button } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";
import { Box, Flex, Text } from "@/components/display/display-primitives";
import { DisplayDurationSliderField } from "@/components/display/display-duration-slider-field";

interface DisplayQuickTimingPanelProps {
  canEdit: boolean;
  isLoading: boolean;
  isSaving: boolean;
  messageDurationSec: number;
  imageDurationSec: number;
  weatherDurationSec: number;
  onMessageDurationSecChange: (value: number) => void;
  onImageDurationSecChange: (value: number) => void;
  onWeatherDurationSecChange: (value: number) => void;
  onSave: () => void;
}

export function DisplayQuickTimingPanel({
  canEdit,
  isLoading,
  isSaving,
  messageDurationSec,
  imageDurationSec,
  weatherDurationSec,
  onMessageDurationSecChange,
  onImageDurationSecChange,
  onWeatherDurationSecChange,
  onSave,
}: DisplayQuickTimingPanelProps) {
  const { t } = useTranslation();

  return (
    <Box
      style={{
        borderRadius: 14,
        border: "1px solid rgba(255,255,255,0.16)",
        background: "rgba(255,255,255,0.06)",
        padding: "0.9rem",
      }}
    >
      <Text as="p" size="2" style={{ margin: 0, marginBottom: "0.45rem", color: "rgba(255,255,255,0.95)", fontWeight: 700 }}>
        {t("display.quick_settings_title")}
      </Text>

      <Text as="p" size="1" style={{ margin: 0, marginBottom: "0.9rem", color: "rgba(255,255,255,0.62)" }}>
        {t("display.quick_settings_help")}
      </Text>

      {isLoading ? (
        <Text size="1" style={{ color: "rgba(255,255,255,0.72)" }}>
          {t("display.quick_settings_loading")}
        </Text>
      ) : !canEdit ? (
        <Text size="1" style={{ color: "rgba(255,255,255,0.72)" }}>
          {t("display.quick_settings_locked")}
        </Text>
      ) : (
        <Flex direction="column" gap="3">
          <DisplayDurationSliderField
            label={t("board.message_duration_label")}
            helpText={t("board.message_duration_help")}
            unitSingular={t("board.duration_unit_singular")}
            unitPlural={t("board.duration_unit_plural")}
            value={messageDurationSec}
            onChange={onMessageDurationSecChange}
          />

          <DisplayDurationSliderField
            label={t("board.image_duration_label")}
            helpText={t("board.image_duration_help")}
            unitSingular={t("board.duration_unit_singular")}
            unitPlural={t("board.duration_unit_plural")}
            value={imageDurationSec}
            onChange={onImageDurationSecChange}
          />

          <DisplayDurationSliderField
            label={t("board.weather_duration_label")}
            helpText={t("board.weather_duration_help")}
            unitSingular={t("board.duration_unit_singular")}
            unitPlural={t("board.duration_unit_plural")}
            value={weatherDurationSec}
            onChange={onWeatherDurationSecChange}
          />

          <Flex justify="end">
            <Button onClick={onSave} disabled={isSaving}>
              {isSaving ? t("board.save_pending") : t("display.quick_settings_save")}
            </Button>
          </Flex>
        </Flex>
      )}
    </Box>
  );
}
